import { create } from 'zustand';
import {
  loginUser,
  registerUser,
  refreshToken as refreshTokenService,
  getUsers,
  updateUser,
  getCurrentUser,
  resetPassword,
  forgotPassword,
} from '../services/auth.service';
import {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RefreshTokenRequest,
  UserSchema,
  UpdateUserRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  AuthTokenPayload,
} from '../types/auth.type';
import { jwtDecode } from 'jwt-decode';
import oneSignalService from '../services/onesignal.service';
import { useNotificationStore } from './notification.store';
import { useOrganizationStore } from './organization.store';
import {
  canAccess,
  getAccessibleOrgIds,
  hasPermission as checkPermission,
  isPlatformAdmin,
  normalizeTokenPayload,
  resolvePermissionList,
} from '../auth';
import {
  clearAnalyticsUser,
  setAnalyticsUser,
  trackLogin,
} from '../helpers/analytics.helper';
import { clearDemoSession, exitDemo, isDemoMode } from '../demo/core';

interface AuthState {
  isAuthenticated: boolean;
  user: UserSchema | null;
  tokenPayload: AuthTokenPayload | null;
  permissionList: string[];
  accessibleOrgIds: string[];
  isPlatformAdmin: boolean;
  token: string | null;
  refreshToken: string | null;
  isLoading: boolean;
  error: string | null;
  login: (credentials: LoginRequest) => Promise<LoginResponse | null>;
  register: (data: RegisterRequest) => Promise<UserSchema | null>;
  logout: () => Promise<void>;
  refreshUserToken: (data: RefreshTokenRequest) => Promise<LoginResponse | null>;
  syncFromToken: (accessToken: string) => void;
  hydrateFromMe: () => Promise<void>;
  hasPermission: (required: string) => boolean;
  canAccessPermission: (
    required: string,
    legacyRoles?: readonly string[],
    orgId?: string | null,
  ) => boolean;
  getOrganizationUsers: (organizationId: string) => Promise<UserSchema[]>;
  updateOrganizationUser: (
    organizationId: string,
    userId: string,
    data: UpdateUserRequest,
  ) => Promise<UserSchema | null>;
  initAuth: () => Promise<void>;
  forgotPassword: (data: ForgotPasswordRequest) => Promise<boolean>;
  resetPassword: (data: ResetPasswordRequest) => Promise<boolean>;
}

interface GenericResponse<T> {
  error?: { message?: string; code?: string } | null;
  response?: { message?: string; code?: string } | null;
  result: T;
}

function buildUserFromToken(
  decoded: Record<string, unknown>,
  payload: AuthTokenPayload,
): UserSchema {
  const data = (decoded.data as UserSchema['data']) ?? {};
  return {
    sub: payload.sub,
    username: payload.username,
    exp: payload.exp,
    iat: payload.iat,
    ...decoded,
    data: {
      ...data,
      permissions: data.permissions ?? payload.permissions,
      sites: data.sites ?? payload.sites,
    },
  } as UserSchema;
}

function getOrganizationIdFromUser(user: UserSchema): string | undefined {
  const perms = user.data?.permissions;
  if (!perms || typeof perms !== 'object' || Array.isArray(perms)) {
    return undefined;
  }
  const orgIds = Object.keys(perms);
  return orgIds.find((id) => id !== '*') || orgIds[0];
}

function scheduleOneSignalRegistration(user: UserSchema, delayMs = 2000): void {
  setTimeout(async () => {
    try {
      if (user.data?.onesignal_player_id) {
        return;
      }

      if (!oneSignalService.isReady()) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }

      const organizationId = getOrganizationIdFromUser(user);
      if (!organizationId || !user.sub) {
        return;
      }

      await oneSignalService.registerPlayerWithBackend(organizationId, user.sub);
      await oneSignalService.setExternalUserId(user.sub);
    } catch (error) {
      console.error('Error registering OneSignal player:', error);
    }
  }, delayMs);
}

function applyAuthFromToken(
  accessToken: string,
  refreshTokenValue: string | null,
  set: (partial: Partial<AuthState> | ((state: AuthState) => Partial<AuthState>)) => void,
  registerPush = true,
): AuthTokenPayload {
  const decoded = jwtDecode<Record<string, unknown>>(accessToken);
  const payload = normalizeTokenPayload(decoded);
  const permissionList = resolvePermissionList(payload);
  const platformAdmin = isPlatformAdmin(payload);
  const user = buildUserFromToken(decoded, payload);

  set({
    token: accessToken,
    refreshToken: refreshTokenValue,
    tokenPayload: payload,
    permissionList,
    accessibleOrgIds: getAccessibleOrgIds(payload),
    isPlatformAdmin: platformAdmin,
    user,
    isAuthenticated: true,
  });

  if (registerPush && !isDemoMode()) {
    scheduleOneSignalRegistration(user);
  }

  return payload;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  isAuthenticated: false,
  user: null,
  tokenPayload: null,
  permissionList: [],
  accessibleOrgIds: [],
  isPlatformAdmin: false,
  token: null,
  refreshToken: null,
  isLoading: false,
  error: null,

  syncFromToken: (accessToken) => {
    const refreshTokenValue = get().refreshToken ?? sessionStorage.getItem('refresh_token');
    applyAuthFromToken(accessToken, refreshTokenValue, set, false);
  },

  hydrateFromMe: async () => {
    try {
      const response = await getCurrentUser();
      const data = response as GenericResponse<UserSchema>;
      if (!data?.result) {
        return;
      }
      const fromApi = data.result;
      set((state) => {
        const mePerms = fromApi.data?.permissions;
        const existingPayload = state.tokenPayload;
        const permissionList = resolvePermissionList(existingPayload, mePerms ?? null);
        const mergedUser: UserSchema = {
          ...fromApi,
          sub: fromApi.sub ?? fromApi.id ?? state.user?.sub ?? '',
          data: {
            ...(fromApi.data ?? {}),
            permissions:
              mePerms &&
              (Array.isArray(mePerms) ? mePerms.length > 0 : Object.keys(mePerms).length > 0)
                ? mePerms
                : state.user?.data?.permissions,
            sites: fromApi.data?.sites ?? state.user?.data?.sites,
          },
        };
        return {
          user: mergedUser,
          permissionList,
          accessibleOrgIds: existingPayload
            ? getAccessibleOrgIds(existingPayload)
            : state.accessibleOrgIds,
        };
      });
    } catch {
      // Keep JWT-derived state if /user/me fails
    }
  },

  hasPermission: (required) => {
    const { permissionList, isPlatformAdmin: platformAdmin } = get();
    if (platformAdmin) {
      return true;
    }
    return checkPermission(permissionList, required);
  },

  canAccessPermission: (required, legacyRoles, orgId) => {
    const { tokenPayload, permissionList } = get();
    return canAccess(tokenPayload, permissionList, required, {
      orgId: orgId ?? undefined,
      legacyRoles,
    });
  },

  login: async (credentials) => {
    // A real sign-in always leaves demo mode first.
    clearDemoSession();
    set({ isLoading: true, error: null });
    try {
      const response = await loginUser(credentials);
      const data = response as GenericResponse<LoginResponse>;

      sessionStorage.setItem('access_token', data.result.access_token);
      sessionStorage.setItem('refresh_token', data.result.refresh_token);

      const payload = applyAuthFromToken(
        data.result.access_token,
        data.result.refresh_token,
        set,
      );
      await get().hydrateFromMe();

      if (payload.sub) {
        setAnalyticsUser(payload.sub);
      }
      trackLogin();

      set({ isLoading: false });
      return data.result;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } };
      set({
        error: err.response?.data?.detail || 'Failed to login',
        isLoading: false,
        isAuthenticated: false,
        token: null,
        refreshToken: null,
        user: null,
        tokenPayload: null,
        permissionList: [],
        accessibleOrgIds: [],
        isPlatformAdmin: false,
      });
      return null;
    }
  },

  register: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await registerUser(data);
      const responseData = response as GenericResponse<UserSchema>;
      set({ isLoading: false });
      return responseData.result;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } };
      set({
        error: err.response?.data?.detail || 'Failed to register',
        isLoading: false,
      });
      return null;
    }
  },

  refreshUserToken: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await refreshTokenService(data);
      const responseData = response as GenericResponse<LoginResponse>;

      sessionStorage.setItem('access_token', responseData.result.access_token);
      sessionStorage.setItem('refresh_token', responseData.result.refresh_token);

      applyAuthFromToken(
        responseData.result.access_token,
        responseData.result.refresh_token,
        set,
        false,
      );
      await get().hydrateFromMe();

      set({ isLoading: false });
      return responseData.result;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } };
      set({
        error: err.response?.data?.detail || 'Failed to refresh token',
        isLoading: false,
      });
      return null;
    }
  },

  getOrganizationUsers: async (organizationId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await getUsers(organizationId);
      const data = response as GenericResponse<UserSchema[]>;
      set({ isLoading: false });
      return data.result;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } };
      set({
        error: err.response?.data?.detail || 'Failed to get users',
        isLoading: false,
      });
      return [];
    }
  },

  updateOrganizationUser: async (organizationId, userId, data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await updateUser(organizationId, userId, data);
      const responseData = response as GenericResponse<UserSchema>;
      set({ isLoading: false });
      return responseData.result;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } };
      set({
        error: err.response?.data?.detail || 'Failed to update user',
        isLoading: false,
      });
      return null;
    }
  },

  logout: async () => {
    const wasDemo = isDemoMode();
    clearAnalyticsUser();

    if (!wasDemo) {
      try {
        await oneSignalService.unregisterPlayerFromBackend();
      } catch (error) {
        console.error('Error unregistering OneSignal player:', error);
      }
    }

    useNotificationStore.getState().clearNotifications();

    const authUser = get().user;
    const userId = authUser?.id ?? authUser?.sub ?? '';
    const orgId =
      useOrganizationStore.getState().selectedOrganization?.id ??
      sessionStorage.getItem('selectedOrganization') ??
      '';

    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('refresh_token');
    sessionStorage.removeItem('selectedOrganization');
    sessionStorage.removeItem('marketHelpGuideBypass');

    try {
      const {
        clearAllOnboardingLaterDismissed,
        clearAllLegacySelfSetupKeys,
        clearOnboardingLiveSession,
      } = await import('../utils/selfSetupStorage');
      const {
        parkActiveOnboardingOnLogout,
        parkAllActiveOnboardingForUser,
        parkAllLiveOnboardingTips,
      } = await import('../utils/selfSetupTour');
      if (userId && orgId) {
        parkActiveOnboardingOnLogout(userId, orgId);
      }
      if (userId) {
        parkAllActiveOnboardingForUser(userId);
      }
      parkAllLiveOnboardingTips();
      clearOnboardingLiveSession();
      clearAllOnboardingLaterDismissed();
      clearAllLegacySelfSetupKeys();
    } catch (error) {
      console.error('Error clearing onboarding storage flags:', error);
    }

    set({
      isAuthenticated: false,
      user: null,
      token: null,
      refreshToken: null,
      tokenPayload: null,
      permissionList: [],
      accessibleOrgIds: [],
      isPlatformAdmin: false,
      error: null,
    });

    // The demo flag is cleared only now, right before the hard navigation, so no
    // window exists where the flag is gone but this demo page is still mounted.
    clearDemoSession();
    // Leaving a demo session reboots the page on /login, so analytics and push
    // (skipped for the whole demo tab) load again for whoever signs in next.
    if (wasDemo) exitDemo();
  },

  initAuth: async () => {
    try {
      set({ isLoading: true });
      const accessToken = sessionStorage.getItem('access_token');
      const refreshTokenValue = sessionStorage.getItem('refresh_token');

      if (!accessToken || !refreshTokenValue) {
        set({ isAuthenticated: false, isLoading: false });
        return;
      }

      try {
        const decoded = jwtDecode<{ exp?: number }>(accessToken);

        if (decoded.exp) {
          const expirationTime = decoded.exp * 1000;

          if (Date.now() >= expirationTime) {
            const response = await refreshTokenService({ refresh_token: refreshTokenValue });
            const responseData = response as GenericResponse<LoginResponse>;

            if (responseData?.result?.access_token) {
              sessionStorage.setItem('access_token', responseData.result.access_token);
              sessionStorage.setItem('refresh_token', responseData.result.refresh_token);
              const payload = applyAuthFromToken(
                responseData.result.access_token,
                responseData.result.refresh_token,
                set,
              );
              await get().hydrateFromMe();
              if (payload.sub) {
                setAnalyticsUser(payload.sub);
              }
              set({ isLoading: false });
              return;
            }

            sessionStorage.removeItem('access_token');
            sessionStorage.removeItem('refresh_token');
            set({ isAuthenticated: false, isLoading: false });
            return;
          }
        }

        const payload = applyAuthFromToken(accessToken, refreshTokenValue, set);
        await get().hydrateFromMe();
        if (payload.sub) {
          setAnalyticsUser(payload.sub);
        }
        set({ isLoading: false });
      } catch (error) {
        console.error('Error checking token expiration:', error);
        sessionStorage.removeItem('access_token');
        sessionStorage.removeItem('refresh_token');
        set({ isAuthenticated: false, isLoading: false });
      }
    } catch (error) {
      console.error('Error initializing auth:', error);
      set({ isAuthenticated: false, isLoading: false });
    }
  },

  forgotPassword: async (data) => {
    set({ isLoading: true, error: null });
    try {
      await forgotPassword(data);
      set({ isLoading: false });
      return true;
    } catch {
      set({ isLoading: false });
      return true;
    }
  },

  resetPassword: async (data) => {
    set({ isLoading: true, error: null });
    try {
      await resetPassword(data);
      set({ isLoading: false });
      return true;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } };
      set({
        error: err.response?.data?.detail || 'Invalid or expired reset link',
        isLoading: false,
      });
      return false;
    }
  },
}));

/** Called from axios interceptor after silent token refresh. */
export function syncAuthStoreFromToken(accessToken: string): void {
  useAuthStore.getState().syncFromToken(accessToken);
}
